/**
 * The desktop shell's tiled windows, as a pure tree: every operation takes a
 * workspace and returns a new one, and ends in `normalize`, so the shapes the UI
 * draws are always the canonical ones (no one-child splits, no same-direction
 * nesting, sizes summing to 1) and the layout is tested without a browser.
 */
import type { SidebarView } from "@/components/board-sidebar";
import { arrange, MINIMIZED_PX } from "@/lib/workspace-arrange";

export type Edge = "left" | "right" | "top" | "bottom";
export type Zone = Edge | "center";
export type Dock = Edge;
/** `row` = children side by side, `column` = stacked. */
export type SplitDir = "row" | "column";
export interface WindowLeaf {
  type: "window";
  id: string;
  /** What the window shows: with tabs, the active tab. */
  view: SidebarView;
  /**
   * A tab group (VS Code's): every view the window holds, in strip order, `view` among
   * them. Present only with two or more; one view is a plain window, as before tabs.
   * The inactive ones stay mounted, hidden, so a chat keeps its draft and scroll.
   */
  tabs?: SidebarView[];
  /**
   * Shown as its title bar only, its neighbours taking the space; kept mounted. Its
   * share in `sizes` is left as it was, so a restore gives back the exact size.
   * Absent is false; at least one window is always open (`normalize`).
   */
  minimized?: boolean;
}
/** `sizes` are fractions of the split, one per child, summing to 1. */
export interface SplitNode {
  type: "split";
  dir: SplitDir;
  children: LayoutNode[];
  sizes: number[];
}
export type LayoutNode = WindowLeaf | SplitNode;
/** Where a closed window sat, so restore can put it back: beside `anchorId` on `zone`, taking `size` of that split. */
export interface ShelfEntry {
  id: string;
  view: SidebarView;
  closedAt: number;
  anchorId: string | null;
  zone: Edge;
  size: number;
  /**
   * It was a tab of window `tabOf`: restored, it goes back in as a tab while that
   * window is open, else it opens beside `anchorId` under that window's id, so the
   * rest of a closed group follows it back in.
   */
  tabOf?: string;
}
export interface NavState {
  dock: Dock;
  hidden: boolean;
}
export interface Workspace {
  version: 1;
  seq: number;
  root: LayoutNode;
  focusedId: string;
  maximizedId: string | null;
  shelf: ShelfEntry[];
  nav: NavState;
  /**
   * The saved layout these windows came from (applied or last saved on this device),
   * so the Windows menu can offer Save — overwrite it — beside Save as. Absent or
   * null: none. It survives window changes on purpose; that is what Save writes back.
   */
  layoutId?: string | null;
}
/**
 * Where a view opens. `tab`: a new tab in window `targetId`, right after its active
 * one and showing — what a plain click does on the desktop, as opening a file does in
 * VS Code. `replace`: in place of the focused window's view (Open here, Restore here,
 * the tab layout).
 */
export type OpenWhere =
  | { kind: "replace" }
  | { kind: "tab"; targetId: string }
  | { kind: "split"; targetId: string; zone: Edge }
  | { kind: "edge"; edge: Edge };
export interface Rect01 {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const WORKSPACE_STORAGE_KEY = "automation.workspace";
export const SHELF_LIMIT = 12;
export const MIN_WINDOW = { width: 320, height: 240 };

const EDGES: readonly Edge[] = ["left", "right", "top", "bottom"];
const VIEW_KINDS = new Set(["board", "queue", "activity", "archive", "settings", "status", "apps", "boards", "waiting", "projects", "changelog", "terminal", "agents", "empty"]);

const axisOf = (edge: Edge): SplitDir => (edge === "left" || edge === "right" ? "row" : "column");
const isAfter = (edge: Edge) => edge === "right" || edge === "bottom";
const leaf = (id: string, view: SidebarView): WindowLeaf => ({ type: "window", id, view });

/** What makes two windows "the same view": opening one already open focuses it instead. */
export function viewKey(view: SidebarView): string {
  if (view.kind === "board") return `board:${view.boardId}`;
  if (view.kind === "agents") return view.agentId ? `agent:${view.agentId}` : "agents";
  if (view.kind === "archive") return "settings";
  return view.kind;
}

// ---- tabs ---------------------------------------------------------------------

/** Every view a window holds, in strip order: its tabs, or its one view. */
export function tabsOf(w: WindowLeaf): SidebarView[] {
  return w.tabs ?? [w.view];
}

/** Which of `tabsOf(w)` is showing. */
export function activeTabIndex(w: WindowLeaf): number {
  const key = viewKey(w.view);
  return Math.max(0, tabsOf(w).findIndex((view) => viewKey(view) === key));
}

/**
 * A window holding `tabs` with `active` showing, canonical: one tab per view (the
 * first kept, the active one where it lands), `tabs` only with two or more.
 */
function withTabs(w: WindowLeaf, tabs: SidebarView[], active: number): WindowLeaf {
  const chosen = tabs[Math.min(Math.max(active, 0), tabs.length - 1)];
  const activeKey = chosen ? viewKey(chosen) : null;
  const seen = new Set<string>();
  const unique: SidebarView[] = [];
  for (const view of tabs) {
    const key = viewKey(view);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(view);
  }
  const { tabs: _old, ...rest } = w;
  if (unique.length === 0) return rest;
  const view = unique.find((v) => viewKey(v) === activeKey) ?? unique[0]!;
  return unique.length > 1 ? { ...rest, view, tabs: unique } : { ...rest, view };
}

/** A stored or edited leaf made canonical: its view among its tabs, no duplicates, no one-tab group. */
function canonicalLeaf(w: WindowLeaf): WindowLeaf {
  if (!w.tabs) return w;
  const key = viewKey(w.view);
  const at = w.tabs.findIndex((view) => viewKey(view) === key);
  // A view not among its tabs (never written so, but read from storage) leads the strip.
  return at < 0 ? withTabs(w, [w.view, ...w.tabs], 0) : withTabs(w, w.tabs, at);
}

/** The window holding a view (as any of its tabs), and which tab. */
export function findView(ws: Workspace, view: SidebarView): { window: WindowLeaf; index: number } | null {
  const key = viewKey(view);
  for (const w of windows(ws)) {
    const index = tabsOf(w).findIndex((tab) => viewKey(tab) === key);
    if (index >= 0) return { window: w, index };
  }
  return null;
}

function updateLeaf(root: LayoutNode, id: string, fn: (w: WindowLeaf) => WindowLeaf): LayoutNode {
  return mapLeaves(root, (w) => (w.id === id ? fn(w) : w));
}

/** Shows tab `index` of window `id`, and brings the window forward (a bar is restored). */
export function activateTab(ws: Workspace, id: string, index: number): Workspace {
  const target = findWindow(ws, id);
  if (!target || index < 0 || index >= tabsOf(target).length) return ws;
  return reveal({ ...ws, root: updateLeaf(ws.root, id, (w) => withTabs(w, tabsOf(w), index)) }, id);
}

/** Ctrl+Tab (`step` 1) / Ctrl+Shift+Tab (-1): the next or previous tab of the focused window, wrapping. */
export function cycleTab(ws: Workspace, step: 1 | -1): Workspace {
  const w = focusedWindow(ws);
  const count = tabsOf(w).length;
  if (count < 2) return ws;
  return activateTab(ws, w.id, (activeTabIndex(w) + step + count) % count);
}

/** Moves tab `from` of window `id` to slot `to` (in the strip without it), the active tab kept. */
export function reorderTab(ws: Workspace, id: string, from: number, to: number): Workspace {
  const target = findWindow(ws, id);
  if (!target) return ws;
  const tabs = [...tabsOf(target)];
  if (from < 0 || from >= tabs.length || from === to) return ws;
  const [moved] = tabs.splice(from, 1);
  tabs.splice(Math.min(Math.max(to, 0), tabs.length), 0, moved!);
  const activeKey = viewKey(target.view);
  return normalize({ ...ws, root: updateLeaf(ws.root, id, (w) => withTabs(w, tabs, tabs.findIndex((v) => viewKey(v) === activeKey))) });
}

/**
 * The tree without `view` wherever it is: one tab out of a group (the tab to its
 * right showing next, else the one to its left, when it was showing), or a window
 * that held only it taken out. `removedId` names such a window.
 */
function withoutView(ws: Workspace, view: SidebarView): { ws: Workspace; removedId: string | null } {
  const found = findView(ws, view);
  if (!found) return { ws, removedId: null };
  const { window: w, index } = found;
  const tabs = tabsOf(w);
  if (tabs.length < 2) {
    const root = removeLeaf(ws.root, w.id);
    if (!root) return { ws, removedId: null };
    return { ws: { ...ws, root, maximizedId: ws.maximizedId === w.id ? null : ws.maximizedId }, removedId: w.id };
  }
  const rest = tabs.filter((_, i) => i !== index);
  const active = activeTabIndex(w);
  const nextActive = active === index ? Math.min(index, rest.length - 1) : active > index ? active - 1 : active;
  return { ws: { ...ws, root: updateLeaf(ws.root, w.id, (leafNow) => withTabs(leafNow, rest, nextActive)) }, removedId: null };
}

/**
 * `view` as a tab of window `targetId`, right after its active tab, and showing. A
 * view open elsewhere moves in (out of its group, or its own window closing), never
 * opened twice; one already in the group is just shown.
 */
export function addTab(ws: Workspace, targetId: string, view: SidebarView): Workspace {
  const target = findWindow(ws, targetId);
  if (!target) return ws;
  const already = tabsOf(target).findIndex((tab) => viewKey(tab) === viewKey(view));
  if (already >= 0) return activateTab(ws, targetId, already);
  const { ws: without, removedId } = withoutView(ws, view);
  const at = activeTabIndex(target) + 1;
  const root = updateLeaf(without.root, targetId, (w) => {
    const tabs = [...tabsOf(w)];
    tabs.splice(at, 0, view);
    return withTabs(unminimized(w), tabs, at);
  });
  const focusedId = removedId && without.focusedId === removedId ? targetId : without.focusedId;
  return focus(normalize({ ...without, root, focusedId }), targetId);
}

/**
 * Window `id` dropped on the centre of `targetId`: all its tabs join the target's,
 * after the active one, its own active tab showing; its spot closes.
 */
export function mergeWindow(ws: Workspace, id: string, targetId: string): Workspace {
  const moving = findWindow(ws, id);
  const target = findWindow(ws, targetId);
  if (!moving || !target || id === targetId) return ws;
  const root = removeLeaf(ws.root, id);
  if (!root) return ws;
  const at = activeTabIndex(target) + 1;
  const incoming = tabsOf(moving);
  const merged = updateLeaf(root, targetId, (w) => {
    const tabs = [...tabsOf(w)];
    tabs.splice(at, 0, ...incoming);
    return withTabs(unminimized(w), tabs, at + activeTabIndex(moving));
  });
  return focus(normalize({ ...ws, root: merged, maximizedId: ws.maximizedId === id ? null : ws.maximizedId, focusedId: targetId }), targetId);
}

/** Where a dragged tab lands: another window's zone, or a workspace edge. */
export type TabDrop = { kind: "window"; id: string; zone: Zone } | { kind: "edge"; edge: Edge };

/**
 * Tab `index` of window `fromId` dragged out of its strip. Onto a window's centre it
 * joins that group; onto a window's side or a workspace edge it becomes a window of
 * its own there. A window's only tab moves the whole window, as its title bar does.
 */
export function moveTab(ws: Workspace, fromId: string, index: number, drop: TabDrop): Workspace {
  const from = findWindow(ws, fromId);
  const view = from ? tabsOf(from)[index] : undefined;
  if (!from || !view) return ws;
  const alone = tabsOf(from).length < 2;
  if (drop.kind === "edge") {
    if (alone) return moveToEdge(ws, fromId, drop.edge);
    const { ws: without } = withoutView(ws, view);
    const [id, bumped] = newId(without);
    return normalize({ ...bumped, root: insertEdge(bumped.root, leaf(id, view), drop.edge), focusedId: id, maximizedId: null });
  }
  if (drop.zone === "center") return drop.id === fromId ? ws : addTab(ws, drop.id, view);
  if (alone) return moveWindow(ws, fromId, drop.id, drop.zone);
  if (!findWindow(ws, drop.id)) return ws;
  const { ws: without } = withoutView(ws, view);
  const [id, bumped] = newId(without);
  const root = updateLeaf(insertBeside(bumped.root, drop.id, leaf(id, view), drop.zone, 1 / 2), drop.id, unminimized);
  return normalize({ ...bumped, root, focusedId: id, maximizedId: null });
}

/**
 * × on a tab: that tab goes to Closed, to come back as a tab of this window. A
 * window's only tab closes the window.
 */
export function closeTab(ws: Workspace, id: string, index: number, now: number): Workspace {
  const w = findWindow(ws, id);
  const view = w ? tabsOf(w)[index] : undefined;
  if (!w || !view) return ws;
  if (tabsOf(w).length < 2) return closeWindow(ws, id, now);
  const { ws: without } = withoutView(ws, view);
  const [shelfId, bumped] = newId(without);
  const entry: ShelfEntry = { id: shelfId, view, closedAt: now, anchorId: id, zone: "right", size: 0.5, tabOf: id };
  return normalize({ ...bumped, shelf: shelve(bumped.shelf, entry) });
}

/** Alt+W: the focused window's active tab, or the window when it has one. */
export function closeActive(ws: Workspace, now: number): Workspace {
  const w = focusedWindow(ws);
  return closeTab(ws, w.id, activeTabIndex(w), now);
}

export function createWorkspace(view: SidebarView): Workspace {
  return {
    version: 1,
    seq: 1,
    root: leaf("w1", view),
    focusedId: "w1",
    maximizedId: null,
    shelf: [],
    nav: { dock: "left", hidden: false },
  };
}

// ---- tree helpers -----------------------------------------------------------

function leavesOf(node: LayoutNode, out: WindowLeaf[] = []): WindowLeaf[] {
  if (node.type === "window") out.push(node);
  else for (const child of node.children) leavesOf(child, out);
  return out;
}

function pathTo(node: LayoutNode, id: string): number[] | null {
  if (node.type === "window") return node.id === id ? [] : null;
  for (let index = 0; index < node.children.length; index++) {
    const rest = pathTo(node.children[index]!, id);
    if (rest) return [index, ...rest];
  }
  return null;
}

function nodeAt(root: LayoutNode, path: number[]): LayoutNode | null {
  let node: LayoutNode | undefined = root;
  for (const index of path) {
    if (!node || node.type !== "split") return null;
    node = node.children[index];
  }
  return node ?? null;
}

function updateAt(root: LayoutNode, path: number[], fn: (node: LayoutNode) => LayoutNode): LayoutNode {
  if (path.length === 0) return fn(root);
  if (root.type !== "split") return root;
  const [head, ...rest] = path;
  return { ...root, children: root.children.map((child, index) => (index === head ? updateAt(child, rest, fn) : child)) };
}

function mapLeaves(node: LayoutNode, fn: (leaf: WindowLeaf) => WindowLeaf): LayoutNode {
  return node.type === "window" ? fn(node) : { ...node, children: node.children.map((child) => mapLeaves(child, fn)) };
}

const split = (dir: SplitDir, children: LayoutNode[], sizes: number[]): SplitNode => ({ type: "split", dir, children, sizes });

/** The tree without one window; null when it was the only one. */
function removeLeaf(root: LayoutNode, id: string): LayoutNode | null {
  const path = pathTo(root, id);
  if (!path) return root;
  if (path.length === 0) return null;
  const index = path.at(-1)!;
  return normalizeNode(
    updateAt(root, path.slice(0, -1), (parent) =>
      parent.type === "split"
        ? { ...parent, children: parent.children.filter((_, i) => i !== index), sizes: parent.sizes.filter((_, i) => i !== index) }
        : parent,
    ),
  );
}

/**
 * Puts `added` beside `targetId` on `zone`. By default it takes `size` of the
 * target's own space; with `ofParent` it takes `size` of the target's parent split
 * when that runs the same way, which is what puts a restored window back exactly.
 */
function insertBeside(root: LayoutNode, targetId: string, added: WindowLeaf, zone: Edge, size: number, ofParent = false): LayoutNode {
  const path = pathTo(root, targetId);
  if (!path) return root;
  const dir = axisOf(zone);
  const after = isAfter(zone);
  if (ofParent && path.length > 0) {
    const parentPath = path.slice(0, -1);
    const parent = nodeAt(root, parentPath);
    if (parent?.type === "split" && parent.dir === dir) {
      const at = path.at(-1)! + (after ? 1 : 0);
      return updateAt(root, parentPath, () => {
        const children = [...parent.children];
        const sizes = parent.sizes.map((s) => s * (1 - size));
        children.splice(at, 0, added);
        sizes.splice(at, 0, size);
        return split(dir, children, sizes);
      });
    }
  }
  return updateAt(root, path, (target) =>
    after ? split(dir, [target, added], [1 - size, size]) : split(dir, [added, target], [size, 1 - size]),
  );
}

/** Along a whole edge: a third, or half beside a lone window. */
function insertEdge(root: LayoutNode, added: WindowLeaf, edge: Edge): LayoutNode {
  const share = root.type === "window" ? 1 / 2 : 1 / 3;
  return isAfter(edge) ? split(axisOf(edge), [root, added], [1 - share, share]) : split(axisOf(edge), [added, root], [share, 1 - share]);
}

function normalizeNode(node: LayoutNode): LayoutNode | null {
  if (node.type === "window") return canonicalLeaf(node);
  const raw = node.sizes;
  const valid = raw.length === node.children.length && raw.every((s) => Number.isFinite(s) && s > 0);
  const own = valid ? raw : node.children.map(() => 1);
  const children: LayoutNode[] = [];
  const sizes: number[] = [];
  node.children.forEach((child, index) => {
    const next = normalizeNode(child);
    if (!next) return;
    if (next.type === "split" && next.dir === node.dir) {
      next.children.forEach((grand, i) => {
        children.push(grand);
        sizes.push(own[index]! * next.sizes[i]!);
      });
    } else {
      children.push(next);
      sizes.push(own[index]!);
    }
  });
  if (children.length === 0) return null;
  if (children.length === 1) return children[0]!;
  const total = sizes.reduce((sum, s) => sum + s, 0);
  return split(node.dir, children, sizes.map((s) => s / total));
}

// ---- reading ----------------------------------------------------------------

export function windows(ws: Workspace): WindowLeaf[] {
  return leavesOf(ws.root);
}

export function windowCount(ws: Workspace): number {
  return windows(ws).length;
}

export function findWindow(ws: Workspace, id: string): WindowLeaf | null {
  return windows(ws).find((w) => w.id === id) ?? null;
}

export function focusedWindow(ws: Workspace): WindowLeaf {
  return findWindow(ws, ws.focusedId) ?? windows(ws)[0]!;
}

/** The next free `w<n>`; skips ids on the shelf, so a shelved window can come back under its own. */
export function newId(ws: Workspace): [string, Workspace] {
  const taken = new Set([...windows(ws).map((w) => w.id), ...ws.shelf.map((e) => e.id)]);
  let seq = ws.seq;
  let id: string;
  do id = `w${++seq}`;
  while (taken.has(id));
  return [id, { ...ws, seq }];
}

export function normalize(ws: Workspace): Workspace {
  let next: Workspace = { ...ws, shelf: ws.shelf.slice(0, SHELF_LIMIT) };
  const root = normalizeNode(ws.root);
  if (root) next.root = root;
  else {
    const [id, bumped] = newId(next);
    next = { ...bumped, root: leaf(id, { kind: "status" }) };
  }
  // Something is always open: with every window minimised the focused one (else the first) comes back.
  const all = windows(next);
  if (all.every((w) => w.minimized)) {
    const back = all.find((w) => w.id === next.focusedId) ?? all[0]!;
    next.root = mapLeaves(next.root, (w) => (w.id === back.id ? unminimized(w) : w));
  }
  const ids = new Set(all.map((w) => w.id));
  if (!ids.has(next.focusedId)) next.focusedId = (windows(next).find((w) => !w.minimized) ?? windows(next)[0]!).id;
  if (next.maximizedId !== null && !ids.has(next.maximizedId)) next.maximizedId = null;
  return next;
}

// ---- opening and closing ----------------------------------------------------

/**
 * Shows `view` in window `id`, in place of its active tab (a view already among its
 * tabs is just shown); a minimised window comes back to show it.
 */
export function setView(ws: Workspace, id: string, view: SidebarView): Workspace {
  return normalize({
    ...ws,
    root: mapLeaves(ws.root, (w) => {
      if (w.id !== id) return w;
      const open = unminimized(w);
      if (!w.tabs) return { ...open, view };
      const tabs = [...w.tabs];
      const at = tabs.findIndex((tab) => viewKey(tab) === viewKey(view));
      if (at >= 0) return withTabs(open, tabs, at);
      const active = activeTabIndex(w);
      tabs[active] = view;
      return withTabs(open, tabs, active);
    }),
  });
}

/**
 * A window with nothing worth keeping a tab for: one view, and that a placeholder —
 * the empty window or Status, the home a fresh workspace and a closed last window
 * show. A tab opened into it takes its place rather than sitting beside it.
 */
function isPlaceholder(w: WindowLeaf): boolean {
  return tabsOf(w).length < 2 && (w.view.kind === "empty" || w.view.kind === "status");
}

/**
 * Tab `from` of window `id` becomes `to`, in its place, showing if `from` was: how a
 * view that cannot be shown (its board deleted, a view not this person's) moves on.
 * Not the window's active tab — a hidden tab redirecting must not replace the one on screen.
 */
export function replaceTab(ws: Workspace, id: string, from: SidebarView, to: SidebarView): Workspace {
  const w = findWindow(ws, id);
  if (!w) return ws;
  const tabs = [...tabsOf(w)];
  const at = tabs.findIndex((view) => viewKey(view) === viewKey(from));
  if (at < 0) return ws;
  tabs[at] = to;
  return normalize({ ...ws, root: updateLeaf(ws.root, id, (leafNow) => withTabs(leafNow, tabs, activeTabIndex(w))) });
}

/** Places a new window; a replace keeps the focused window's id and shows the view there. */
function place(ws: Workspace, added: WindowLeaf, where: OpenWhere): Workspace {
  if (where.kind === "replace") return setView(ws, focusedWindow(ws).id, added.view);
  if (where.kind === "tab") return openAsTab(ws, where.targetId, added.view);
  const root =
    where.kind === "split"
      ? insertBeside(ws.root, findWindow(ws, where.targetId) ? where.targetId : focusedWindow(ws).id, added, where.zone, 1 / 2)
      : insertEdge(ws.root, added, where.edge);
  return normalize({ ...ws, root, focusedId: added.id, maximizedId: null });
}

export function openView(ws: Workspace, view: SidebarView, where: OpenWhere, opts: { duplicate?: boolean } = {}): Workspace {
  const key = viewKey(view);
  if (where.kind === "replace" && viewKey(focusedWindow(ws).view) === key) return setView(ws, ws.focusedId, view);
  if (!opts.duplicate) {
    // Open already, maybe as a tab behind another: that tab is brought forward.
    const open = findView(ws, view);
    if (open) return activateTab(ws, open.window.id, open.index);
  }
  if (where.kind === "replace") return setView(ws, focusedWindow(ws).id, view);
  if (where.kind === "tab") return openAsTab(ws, where.targetId, view);
  const [id, bumped] = newId(ws);
  return place(bumped, leaf(id, view), where);
}

/** `view` as a new tab of `targetId` (the focused window when that is gone), or in place of a lone placeholder. */
function openAsTab(ws: Workspace, targetId: string, view: SidebarView): Workspace {
  const target = findWindow(ws, targetId) ?? focusedWindow(ws);
  if (isPlaceholder(target)) return focus(setView(ws, target.id, view), target.id);
  return addTab(ws, target.id, view);
}

/** Shelf with `entry` in front, older entries for the same view (or id) dropped, capped. */
function shelve(shelf: ShelfEntry[], entry: ShelfEntry): ShelfEntry[] {
  const key = viewKey(entry.view);
  return [entry, ...shelf.filter((e) => e.id !== entry.id && viewKey(e.view) !== key)].slice(0, SHELF_LIMIT);
}

/**
 * A window to the shelf: `entry` for its active view, and for a group one entry per
 * other tab (fresh ids, the same place, `tabOf` the window), so whichever comes back
 * first reopens the window under its id and the others rejoin it as tabs.
 */
function shelveWindow(ws: Workspace, closing: WindowLeaf, entry: ShelfEntry): Workspace {
  if (!closing.tabs) return { ...ws, shelf: shelve(ws.shelf, entry) };
  let next = ws;
  const active = activeTabIndex(closing);
  // The others first, so the active tab heads the Closed list.
  closing.tabs.forEach((view, index) => {
    if (index === active) return;
    const [tabId, bumped] = newId(next);
    next = { ...bumped, shelf: shelve(bumped.shelf, { ...entry, id: tabId, view, tabOf: closing.id }) };
  });
  return { ...next, shelf: shelve(next.shelf, { ...entry, tabOf: closing.id }) };
}

export function closeWindow(ws: Workspace, id: string, now: number): Workspace {
  const closing = findWindow(ws, id);
  const path = pathTo(ws.root, id);
  if (!closing || !path) return ws;
  if (path.length === 0) {
    const shelved = shelveWindow(ws, closing, { id, view: closing.view, closedAt: now, anchorId: null, zone: "right", size: 0.5 });
    const [fresh, bumped] = newId(shelved);
    return normalize({ ...bumped, root: leaf(fresh, { kind: "status" }), focusedId: fresh, maximizedId: null });
  }
  const parent = nodeAt(ws.root, path.slice(0, -1)) as SplitNode;
  const index = path.at(-1)!;
  const before = index > 0;
  const neighbour = parent.children[before ? index - 1 : index + 1]!;
  const anchor = before ? leavesOf(neighbour).at(-1)! : leavesOf(neighbour)[0]!;
  const zone: Edge = parent.dir === "row" ? (before ? "right" : "left") : before ? "bottom" : "top";
  const entry: ShelfEntry = { id, view: closing.view, closedAt: now, anchorId: anchor.id, zone, size: parent.sizes[index]! };
  const shelved = shelveWindow(ws, closing, entry);
  return normalize({
    ...shelved,
    root: removeLeaf(ws.root, id)!,
    focusedId: ws.focusedId === id ? (anchor.minimized ? nearestOpen(ws, id) ?? anchor.id : anchor.id) : ws.focusedId,
    maximizedId: ws.maximizedId === id ? null : ws.maximizedId,
  });
}

/** Back from the shelf, under its own id when free, where it was unless told otherwise. */
export function restoreWindow(ws: Workspace, shelfId: string, where?: OpenWhere): Workspace {
  const entry = ws.shelf.find((e) => e.id === shelfId);
  if (!entry) return ws;
  const rest = { ...ws, shelf: ws.shelf.filter((e) => e !== entry) };
  const open = findView(rest, entry.view);
  if (open) return activateTab(rest, open.window.id, open.index);
  // A closed tab rejoins its group while the group is open.
  if (!where && entry.tabOf && findWindow(rest, entry.tabOf)) return addTab(rest, entry.tabOf, entry.view);
  // Its group closed too: it reopens under the group's id, for the rest to rejoin.
  let ownId = entry.id;
  if (!where && entry.tabOf && !findWindow(rest, entry.tabOf)) {
    ownId = entry.tabOf;
    // The group's own entry, still on the shelf, gives up the id (it rejoins by `tabOf`).
    const holder = rest.shelf.find((e) => e.id === ownId);
    if (holder) {
      const [fresh, renumbered] = newId(rest);
      rest.seq = renumbered.seq;
      rest.shelf = rest.shelf.map((e) => (e === holder ? { ...e, id: fresh } : e));
    }
  }
  const [id, bumped] = findWindow(rest, ownId) ? newId(rest) : [ownId, rest];
  const added = leaf(id, entry.view);
  if (where) return place(bumped, added, where);
  if (entry.anchorId && findWindow(bumped, entry.anchorId)) {
    const root = insertBeside(bumped.root, entry.anchorId, added, entry.zone, entry.size, true);
    return normalize({ ...bumped, root, focusedId: id, maximizedId: null });
  }
  return place(bumped, added, { kind: "edge", edge: "right" });
}

export function clearShelf(ws: Workspace): Workspace {
  return normalize({ ...ws, shelf: [] });
}

// ---- rearranging ------------------------------------------------------------

export function swapWindows(ws: Workspace, a: string, b: string): Workspace {
  const first = findWindow(ws, a);
  const second = findWindow(ws, b);
  if (!first || !second || a === b) return ws;
  return normalize({ ...ws, root: mapLeaves(ws.root, (w) => (w.id === a ? second : w.id === b ? first : w)) });
}

/**
 * Centre makes it tabs of the target (`mergeWindow`; swapping is the title bar
 * menu's); an edge takes the window out and puts it beside the target, half each —
 * both shown, since a drop beside a bar means to see the two.
 */
export function moveWindow(ws: Workspace, id: string, targetId: string, zone: Zone): Workspace {
  const moving = findWindow(ws, id);
  if (id === targetId || !moving || !findWindow(ws, targetId)) return ws;
  if (zone === "center") return mergeWindow(ws, id, targetId);
  const without = mapLeaves(removeLeaf(ws.root, id)!, (w) => (w.id === targetId ? unminimized(w) : w));
  return normalize({ ...ws, root: insertBeside(without, targetId, unminimized(moving), zone, 1 / 2), focusedId: id });
}

export function moveToEdge(ws: Workspace, id: string, edge: Edge): Workspace {
  const moving = findWindow(ws, id);
  if (!moving || windowCount(ws) < 2) return ws;
  return normalize({ ...ws, root: insertEdge(removeLeaf(ws.root, id)!, unminimized(moving), edge), focusedId: id });
}

/** The pixels a node needs along `dir`: windows side by side add up, windows across take the widest. */
function minExtent(node: LayoutNode, dir: SplitDir): number {
  if (node.type === "window") return node.minimized ? MINIMIZED_PX : dir === "row" ? MIN_WINDOW.width : MIN_WINDOW.height;
  const each = node.children.map((child) => minExtent(child, dir));
  return node.dir === dir ? each.reduce((sum, n) => sum + n, 0) : Math.max(...each);
}

/**
 * Moves the boundary between `children[index]` and `children[index + 1]` of the
 * split at `path` to `boundary` (a fraction of the split from its start). Only
 * those two change, each kept at its minimum for `extentPx` of split; when they
 * cannot both fit, they share their space in proportion to their minimums.
 */
export function resize(ws: Workspace, path: number[], index: number, boundary: number, extentPx: number): Workspace {
  const node = nodeAt(ws.root, path);
  if (node?.type !== "split" || index < 0 || index + 1 >= node.children.length || !Number.isFinite(boundary)) return ws;
  const start = node.sizes.slice(0, index).reduce((sum, s) => sum + s, 0);
  const combined = node.sizes[index]! + node.sizes[index + 1]!;
  const px = extentPx > 0 ? extentPx : Infinity;
  const minA = minExtent(node.children[index]!, node.dir) / px;
  const minB = minExtent(node.children[index + 1]!, node.dir) / px;
  const first =
    minA + minB > combined ? (combined * minA) / (minA + minB) : Math.min(Math.max(boundary - start, minA), combined - minB);
  const sizes = [...node.sizes];
  sizes[index] = first;
  sizes[index + 1] = combined - first;
  return normalize({ ...ws, root: updateAt(ws.root, path, () => ({ ...node, sizes })) });
}

/**
 * After a window is added: when any window is now below its minimum for a workspace
 * of `width`×`height` px, the windows are re-tiled into an even grid (as many columns
 * as fit, in tree order) so every one gets room, rather than the newest being a sliver.
 * Anything that already fits, or a maximised workspace, is left as the user arranged it.
 */
export function fitWindows(ws: Workspace, width: number, height: number): Workspace {
  const list = windows(ws);
  if (ws.maximizedId || list.length < 2 || width <= 0 || height <= 0) return ws;
  // Only open windows count: a minimised one is meant to be a bar.
  const squeezed = [...arrange(ws.root, { width, height }).frames.values()].some(
    (r) => !r.bar && (r.w < MIN_WINDOW.width - 1 || r.h < MIN_WINDOW.height - 1),
  );
  if (!squeezed) return ws;
  const fit = Math.max(1, Math.floor(width / MIN_WINDOW.width));
  const rowCount = Math.ceil(list.length / Math.min(list.length, fit));
  const perRow = Math.ceil(list.length / rowCount);
  const rows: LayoutNode[] = [];
  for (let i = 0; i < list.length; i += perRow) {
    const items = list.slice(i, i + perRow);
    rows.push(split("row", items, items.map(() => 1 / items.length)));
  }
  return normalize({ ...ws, root: split("column", rows, rows.map(() => 1 / rows.length)) });
}

/** Equal shares for the split's open children; a minimised one keeps its stored share for its restore. */
export function equalize(ws: Workspace, path: number[]): Workspace {
  const node = nodeAt(ws.root, path);
  if (node?.type !== "split") return ws;
  const open = node.children.map((child) => !collapsed(child));
  const count = open.filter(Boolean).length;
  if (count === 0) return ws;
  const share = node.sizes.reduce((sum, size, index) => (open[index] ? sum + size : sum), 0) / count;
  const sizes = node.sizes.map((size, index) => (open[index] ? share : size));
  return normalize({ ...ws, root: updateAt(ws.root, path, () => ({ ...node, sizes })) });
}

/**
 * Every view open now that `keep` does not name, to the shelf, unanchored (the
 * windows are being replaced): a window under its id, a group's other tabs under
 * fresh ids with `tabOf`, so they come back together.
 */
function shelveAllBut(ws: Workspace, keep: Set<string>, now: number): Workspace {
  let next: Workspace = { ...ws, shelf: ws.shelf.filter((e) => !keep.has(viewKey(e.view))) };
  for (const w of [...windows(ws)].reverse()) {
    const leaving = tabsOf(w).filter((view) => !keep.has(viewKey(view)));
    if (leaving.length === 0) continue;
    const grouped = leaving.length > 1 ? { tabOf: w.id } : {};
    const activeKey = viewKey(w.view);
    for (const view of leaving.filter((v) => viewKey(v) !== activeKey)) {
      const [tabId, bumped] = newId(next);
      next = { ...bumped, shelf: shelve(bumped.shelf, { id: tabId, view, closedAt: now, anchorId: null, zone: "right", size: 0.5, ...grouped }) };
    }
    if (!keep.has(activeKey)) {
      next = { ...next, shelf: shelve(next.shelf, { id: w.id, view: w.view, closedAt: now, anchorId: null, zone: "right", size: 0.5, ...grouped }) };
    }
  }
  return next;
}

/** Replaces the layout with two windows, half each; what was open goes to the shelf unless it is one of the two. */
export function presetSideBySide(ws: Workspace, left: SidebarView, right: SidebarView, now = Date.now()): Workspace {
  const keep = new Set([viewKey(left), viewKey(right)]);
  const [leftId, a] = newId(shelveAllBut(ws, keep, now));
  const [rightId, b] = newId(a);
  return normalize({ ...b, root: split("row", [leaf(leftId, left), leaf(rightId, right)], [0.5, 0.5]), focusedId: leftId, maximizedId: null });
}

// ---- saved layouts ------------------------------------------------------------

/**
 * What a saved layout holds (`workspace_layouts` on the server): the tree with its
 * windows renumbered `w1…` in order (ids mean nothing outside this device; applying
 * gives fresh ones), the retired `archive` view written as Settings, and the nav.
 * The shelf, focus and maximised window are this device's moment, not the layout.
 */
export function layoutFromWorkspace(ws: Workspace): { layout: LayoutNode; nav: NavState } {
  // An empty window is a moment's placeholder, not part of an arrangement: left out.
  let tree: LayoutNode | null = ws.root;
  for (const w of windows(ws)) if (tree && tabsOf(w).every((view) => view.kind === "empty")) tree = removeLeaf(tree, w.id);
  let seq = 0;
  const stored = (view: SidebarView): SidebarView => (view.kind === "archive" ? { kind: "settings" } : view);
  const copyLeaf = (w: WindowLeaf): WindowLeaf => {
    const tabs = tabsOf(w).filter((view) => view.kind !== "empty");
    const active = Math.max(0, tabs.findIndex((view) => viewKey(view) === viewKey(w.view)));
    return withMinimized(withTabs(leaf(`w${++seq}`, stored(tabs[active]!)), tabs.map(stored), active), w.minimized);
  };
  const copy = (node: LayoutNode): LayoutNode =>
    node.type === "window" ? copyLeaf(node) : split(node.dir, node.children.map(copy), [...node.sizes]);
  return { layout: (tree && normalizeNode(copy(tree))) ?? leaf("w1", { kind: "status" }), nav: { ...ws.nav } };
}

/** A stored tree read back, every window minted a new id; null when it is not a tree at all. */
function readLayoutTree(value: unknown, mint: () => string, depth = 0): LayoutNode | null {
  if (!isObject(value) || depth > 12) return null;
  if (value.type === "window") {
    const view = parseView(value.view);
    return view ? withMinimized(readTabs(leaf(mint(), view), value.tabs), value.minimized === true) : null;
  }
  if (value.type !== "split" || (value.dir !== "row" && value.dir !== "column")) return null;
  const { children, sizes } = value;
  if (!Array.isArray(children) || children.length === 0) return null;
  const parsed: LayoutNode[] = [];
  for (const child of children) {
    const node = readLayoutTree(child, mint, depth + 1);
    if (!node) return null;
    parsed.push(node);
  }
  // Bad sizes are not worth losing the layout over: `normalize` shares the split equally instead.
  const own = Array.isArray(sizes) && sizes.length === children.length && sizes.every((s) => typeof s === "number") ? (sizes as number[]) : parsed.map(() => 1);
  return split(value.dir, parsed, own);
}

/**
 * Replaces the windows with a saved layout's, focusing its first. A window whose
 * board or agent is gone (`exists` says so) opens as Status rather than a broken
 * view. The windows it replaces go to Closed — the same as the presets, so the
 * undo for "applied the wrong one" is the shelf — bar any whose view the layout
 * opens again. The navigation moves only when the layout carries a nav. A layout
 * that does not read as a tree changes nothing.
 */
export function applyLayout(
  ws: Workspace,
  saved: { id?: string; layout: unknown; nav?: NavState | null },
  exists: (view: SidebarView) => boolean = () => true,
  now = Date.now(),
): Workspace {
  let next = ws;
  const mint = () => {
    const [id, bumped] = newId(next);
    next = bumped;
    return id;
  };
  // Mint against the current windows and the shelf, so a new id never collides with one being shelved.
  const read = readLayoutTree(saved.layout, mint);
  const live = (view: SidebarView): SidebarView => (exists(view) ? view : { kind: "status" });
  const root = read ? normalizeNode(mapLeaves(read, (w) => withTabs(w, tabsOf(w).map(live), activeTabIndex(w)))) : null;
  if (!root) return ws;
  const keep = new Set(leavesOf(root).flatMap((w) => tabsOf(w).map(viewKey)));
  // Seq already past every id minted for the layout, so the shelf's fresh ids never meet them.
  const { shelf, seq } = shelveAllBut(next, keep, now);
  const nav = saved.nav && isEdge(saved.nav.dock) ? { dock: saved.nav.dock, hidden: Boolean(saved.nav.hidden) } : ws.nav;
  const first = leavesOf(root).find((w) => !w.minimized) ?? leavesOf(root)[0]!;
  return normalize({ ...next, seq, root, shelf, focusedId: first.id, maximizedId: null, nav, layoutId: saved.id ?? null });
}

/** Marks which saved layout the windows are (after a save), or none (a preset, a reset). */
export function setActiveLayout(ws: Workspace, layoutId: string | null): Workspace {
  return (ws.layoutId ?? null) === layoutId ? ws : { ...ws, layoutId };
}

/**
 * The saved layout Alt+L (`step` 1) or Alt+Shift+L (`step` -1) lands on: the next or
 * previous in list order after the active one, wrapping. With none active (or the
 * active one deleted) it is the first going forward and the last going back. Null
 * with nothing saved, or when the one layout is already applied.
 */
export function cycleLayoutId(ids: readonly string[], activeId: string | null | undefined, step: 1 | -1): string | null {
  if (ids.length === 0) return null;
  const at = activeId ? ids.indexOf(activeId) : -1;
  if (at < 0) return step === 1 ? ids[0]! : ids[ids.length - 1]!;
  const next = ids[(at + step + ids.length) % ids.length]!;
  return next === activeId ? null : next;
}

/** What a keydown means for the layout cycle: Alt+L forward, Alt+Shift+L back, else nothing. */
export function layoutCycleKey(event: {
  code: string;
  altKey: boolean;
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  /** The key went to a text field, where Option+L types a character on a Mac. */
  editable: boolean;
}): 1 | -1 | null {
  if (event.code !== "KeyL" || !event.altKey || event.ctrlKey || event.metaKey || event.editable) return null;
  return event.shiftKey ? -1 : 1;
}

/** Alt+N, a new empty window: never Shift/Ctrl/Cmd, never in a text field (Option+N starts ñ there on a Mac). */
export function isEmptyWindowKey(event: {
  code: string;
  altKey: boolean;
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  editable: boolean;
}): boolean {
  return event.code === "KeyN" && event.altKey && !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.editable;
}

/** A new empty window to the right of the focused one: a picker that becomes whatever is chosen in it. */
export function openEmptyWindow(ws: Workspace): Workspace {
  return openView(ws, { kind: "empty" }, { kind: "split", targetId: ws.focusedId, zone: "right" }, { duplicate: true });
}

// ---- the agents list and its chats ----------------------------------------------

/** The list's share of the width when a chat opens beside it: about the old list pane's. */
export const AGENT_LIST_SHARE = 0.3;

const isAgentChat = (view: SidebarView) => view.kind === "agents" && Boolean(view.agentId);

/**
 * An agent picked in an Agents list window (`fromId`). On the desktop the list and a
 * chat are separate windows, so the chat never carries a second copy of the list.
 * An agent already open is focused. A plain pick opens the agent as a new tab of the
 * chat window nearest the list, beside the chat it shows, so clicking down the list
 * never piles up windows and never loses a chat. `beside` (Ctrl/Cmd-click, "Open in
 * new window") opens a window of its own right of that chat, taking half its space.
 * With no chat open either way, a chat opens to the list's right, the list keeping
 * `AGENT_LIST_SHARE` — one window then reads as list | chat.
 */
export function openAgentChat(
  ws: Workspace,
  fromId: string,
  agentId: string,
  opts: { beside?: boolean } = {},
): Workspace {
  const view: SidebarView = { kind: "agents", agentId };
  const open = findView(ws, view);
  if (open) return activateTab(ws, open.window.id, open.index);
  const from = findWindow(ws, fromId) ?? focusedWindow(ws);
  const [id, bumped] = newId(ws);
  const nearest = nearestChat(ws, from.id);
  if (nearest && opts.beside) return place(bumped, leaf(id, view), { kind: "split", targetId: nearest.id, zone: "right" });
  // A plain pick is a new tab of that chat window, beside the chat it was showing, as any open is.
  if (nearest) return addTab(ws, nearest.id, view);
  const root = insertBeside(bumped.root, from.id, leaf(id, view), "right", 1 - AGENT_LIST_SHARE);
  return normalize({ ...bumped, root, focusedId: id, maximizedId: null });
}

/** The Agents list asked for from a chat window: focused where it is open, else opened to the chat's left. */
export function showAgentList(ws: Workspace, chatId: string): Workspace {
  const open = findView(ws, { kind: "agents" });
  if (open) return activateTab(ws, open.window.id, open.index);
  const chat = findWindow(ws, chatId) ?? focusedWindow(ws);
  const [id, bumped] = newId(ws);
  // A share of the whole row the chat sits in, so beside a half-width chat the list is not a sliver.
  const root = insertBeside(bumped.root, chat.id, leaf(id, { kind: "agents" }), "left", AGENT_LIST_SHARE, true);
  return normalize({ ...bumped, root, focusedId: id, maximizedId: null });
}

/** Which agent's chat sits nearest a list window — the one its list marks as open. */
export function agentChatNear(ws: Workspace, listId: string): string | null {
  const view = nearestChat(ws, listId)?.view;
  return view?.kind === "agents" ? view.agentId ?? null : null;
}

/** The agent chat window whose centre is closest to window `fromId`'s. */
function nearestChat(ws: Workspace, fromId: string): WindowLeaf | null {
  const rects = layoutRects(ws.root);
  const origin = rects.get(fromId);
  if (!origin) return null;
  const centre = (r: Rect01) => [r.x + r.w / 2, r.y + r.h / 2] as const;
  const [ox, oy] = centre(origin);
  let best: { window: WindowLeaf; distance: number } | null = null;
  for (const w of windows(ws)) {
    if (!isAgentChat(w.view)) continue;
    const [x, y] = centre(rects.get(w.id)!);
    const distance = Math.hypot(x - ox, y - oy);
    if (!best || distance < best.distance) best = { window: w, distance };
  }
  return best?.window ?? null;
}

// ---- focus, geometry, maximize, nav -------------------------------------------

/** Focusing another window while one is maximized maximizes that one, so focus never lands out of sight. */
export function focus(ws: Workspace, id: string): Workspace {
  if (!findWindow(ws, id)) return ws;
  // Maximised, a bar would cover the screen with nothing: it comes back.
  const root = ws.maximizedId ? setMinimized(ws.root, id, false) : ws.root;
  return normalize({ ...ws, root, focusedId: id, maximizedId: ws.maximizedId ? id : null });
}

/** 1-based, in tree order, as Alt+1…9 count; a minimised window is restored, since it was asked for. */
export function focusIndex(ws: Workspace, n: number): Workspace {
  const target = windows(ws)[n - 1];
  return target && n >= 1 ? reveal(ws, target.id) : ws;
}

export function layoutRects(root: LayoutNode): Map<string, Rect01> {
  const out = new Map<string, Rect01>();
  const walk = (node: LayoutNode, rect: Rect01) => {
    if (node.type === "window") return void out.set(node.id, rect);
    let offset = 0;
    node.children.forEach((child, index) => {
      const share = node.sizes[index]!;
      walk(
        child,
        node.dir === "row"
          ? { x: rect.x + offset * rect.w, y: rect.y, w: share * rect.w, h: rect.h }
          : { x: rect.x, y: rect.y + offset * rect.h, w: rect.w, h: share * rect.h },
      );
      offset += share;
    });
  };
  walk(root, { x: 0, y: 0, w: 1, h: 1 });
  return out;
}

const EPS = 1e-6;

/** The nearest open window that way which overlaps the focused one on the other axis. */
export function focusDirection(ws: Workspace, dir: Edge): Workspace {
  const id = nearestWindow(ws, ws.focusedId, dir);
  return id ? focus(ws, id) : ws;
}

/** The nearest window that way from `fromId`, skipping minimised windows (by stored shares, which is the arrangement's order). */
function nearestWindow(ws: Workspace, fromId: string, dir: Edge): string | null {
  const rects = layoutRects(ws.root);
  const from = rects.get(fromId);
  if (!from) return null;
  const minimized = new Set(windows(ws).filter((w) => w.minimized).map((w) => w.id));
  const horizontal = dir === "left" || dir === "right";
  let best: { id: string; gap: number; skew: number } | null = null;
  for (const [id, r] of rects) {
    if (id === fromId || minimized.has(id)) continue;
    const gap =
      dir === "right" ? r.x - (from.x + from.w) : dir === "left" ? from.x - (r.x + r.w) : dir === "bottom" ? r.y - (from.y + from.h) : from.y - (r.y + r.h);
    const overlaps = horizontal ? r.y < from.y + from.h - EPS && r.y + r.h > from.y + EPS : r.x < from.x + from.w - EPS && r.x + r.w > from.x + EPS;
    if (gap < -EPS || !overlaps) continue;
    const skew = horizontal ? Math.abs(r.y - from.y) : Math.abs(r.x - from.x);
    if (!best || gap < best.gap - EPS || (Math.abs(gap - best.gap) <= EPS && skew < best.skew)) best = { id, gap, skew };
  }
  return best?.id ?? null;
}

/** The open window nearest `id`: right, below, left, above, else any. */
function nearestOpen(ws: Workspace, id: string): string | null {
  for (const dir of ["right", "bottom", "left", "top"] as const) {
    const found = nearestWindow(ws, id, dir);
    if (found) return found;
  }
  return windows(ws).find((w) => w.id !== id && !w.minimized)?.id ?? null;
}

/** A minimised window comes back first: maximising a bar means to see it. */
export function maximize(ws: Workspace, id: string): Workspace {
  if (!findWindow(ws, id)) return ws;
  return normalize({ ...ws, root: setMinimized(ws.root, id, false), maximizedId: id, focusedId: id });
}

export function unmaximize(ws: Workspace): Workspace {
  return normalize({ ...ws, maximizedId: null });
}

export function toggleMaximize(ws: Workspace, id: string): Workspace {
  return ws.maximizedId === id ? unmaximize(ws) : maximize(ws, id);
}

// ---- minimise ------------------------------------------------------------------

function unminimized(w: WindowLeaf): WindowLeaf {
  if (!w.minimized) return w;
  const { minimized: _gone, ...rest } = w;
  return rest;
}

const withMinimized = (w: WindowLeaf, minimized: boolean | undefined): WindowLeaf => (minimized ? { ...w, minimized: true } : w);

function setMinimized(root: LayoutNode, id: string, minimized: boolean): LayoutNode {
  return mapLeaves(root, (w) => (w.id === id ? withMinimized(unminimized(w), minimized) : w));
}

/** A minimised window, or a split of nothing but minimised windows. */
function collapsed(node: LayoutNode): boolean {
  return node.type === "window" ? Boolean(node.minimized) : node.children.every(collapsed);
}

export function isMinimized(ws: Workspace, id: string): boolean {
  return Boolean(findWindow(ws, id)?.minimized);
}

/** Whether `minimize` would do anything: never the last open window, nor one alone without a title bar. */
export function canMinimize(ws: Workspace, id: string): boolean {
  const target = findWindow(ws, id);
  return Boolean(target && !target.minimized && windows(ws).some((w) => w.id !== id && !w.minimized));
}

/**
 * Down to its title bar, its neighbours taking the space; its stored share is kept
 * for the restore. The last open window stays open (a no-op: the UI says so). Focus
 * on it moves to the nearest open window; a maximised one is un-maximised first.
 */
export function minimize(ws: Workspace, id: string): Workspace {
  if (!canMinimize(ws, id)) return ws;
  const focusedId = ws.focusedId === id ? nearestOpen(ws, id) ?? ws.focusedId : ws.focusedId;
  return normalize({ ...ws, root: setMinimized(ws.root, id, true), focusedId, maximizedId: ws.maximizedId === id ? null : ws.maximizedId });
}

/** Back to its stored size, and focused. */
export function restore(ws: Workspace, id: string): Workspace {
  if (!findWindow(ws, id)) return ws;
  return focus({ ...ws, root: setMinimized(ws.root, id, false) }, id);
}

export function toggleMinimize(ws: Workspace, id: string): Workspace {
  return isMinimized(ws, id) ? restore(ws, id) : minimize(ws, id);
}

export function restoreAll(ws: Workspace): Workspace {
  return normalize({ ...ws, root: mapLeaves(ws.root, unminimized) });
}

/** Focus a window because it was asked for by name (a menu, Alt+n, its view opened again): a bar is restored. */
export function reveal(ws: Workspace, id: string): Workspace {
  return isMinimized(ws, id) ? restore(ws, id) : focus(ws, id);
}

/** Alt+M, minimise or restore the focused window: never Shift/Ctrl/Cmd, never in a text field (Option+M types µ on a Mac). */
export function isMinimizeKey(event: {
  code: string;
  altKey: boolean;
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  editable: boolean;
}): boolean {
  return event.code === "KeyM" && event.altKey && !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.editable;
}

export function setNav(ws: Workspace, patch: Partial<NavState>): Workspace {
  return normalize({ ...ws, nav: { ...ws.nav, ...patch } });
}

// ---- storage ------------------------------------------------------------------

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);
const isEdge = (value: unknown): value is Edge => EDGES.includes(value as Edge);

/**
 * A stored view, checked and brought up to date: a queue drops the assignee it
 * once carried (the queue is always the user's own), and the archive is Settings.
 */
function parseView(value: unknown): SidebarView | null {
  if (!isObject(value) || typeof value.kind !== "string" || !VIEW_KINDS.has(value.kind)) return null;
  if (value.kind === "board") return typeof value.boardId === "string" && value.boardId ? { kind: "board", boardId: value.boardId } : null;
  if (value.kind === "agents") {
    if (value.agentId === undefined) return { kind: "agents" };
    return typeof value.agentId === "string" && value.agentId ? { kind: "agents", agentId: value.agentId } : null;
  }
  if (value.kind === "archive") return { kind: "settings" };
  return { kind: value.kind } as SidebarView;
}

/** A stored `tabs` list onto a leaf: bad entries dropped, the rest made canonical (`view` stays the active one). */
function readTabs(w: WindowLeaf, value: unknown): WindowLeaf {
  if (!Array.isArray(value)) return w;
  const tabs = value.map(parseView).filter((view): view is SidebarView => view !== null);
  return tabs.length ? canonicalLeaf({ ...w, tabs }) : w;
}

function parseNode(value: unknown, ids: Set<string>): LayoutNode | null {
  if (!isObject(value)) return null;
  if (value.type === "window") {
    const view = parseView(value.view);
    if (typeof value.id !== "string" || !value.id || ids.has(value.id) || !view) return null;
    ids.add(value.id);
    return withMinimized(readTabs(leaf(value.id, view), value.tabs), value.minimized === true);
  }
  if (value.type !== "split" || (value.dir !== "row" && value.dir !== "column")) return null;
  const { children, sizes } = value;
  if (!Array.isArray(children) || !Array.isArray(sizes) || sizes.length !== children.length) return null;
  if (!sizes.every((s) => typeof s === "number")) return null;
  const parsed: LayoutNode[] = [];
  for (const child of children) {
    const node = parseNode(child, ids);
    if (!node) return null;
    parsed.push(node);
  }
  return split(value.dir, parsed, sizes as number[]);
}

/** A bad shelf entry is dropped rather than costing the whole layout. */
function parseShelf(value: unknown, ids: Set<string>): ShelfEntry[] {
  if (!Array.isArray(value)) return [];
  const out: ShelfEntry[] = [];
  for (const e of value) {
    if (!isObject(e)) continue;
    const view = parseView(e.view);
    if (!view || typeof e.id !== "string" || !e.id || ids.has(e.id) || out.some((o) => o.id === e.id)) continue;
    if (typeof e.closedAt !== "number" || !isEdge(e.zone) || typeof e.size !== "number" || !(e.size > 0 && e.size < 1)) continue;
    if (e.anchorId !== null && typeof e.anchorId !== "string") continue;
    const tabOf = typeof e.tabOf === "string" && e.tabOf ? { tabOf: e.tabOf } : {};
    out.push({ id: e.id, view, closedAt: e.closedAt, anchorId: e.anchorId, zone: e.zone, size: e.size, ...tabOf });
  }
  return out;
}

function parseStored(raw: string): Workspace | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isObject(data) || data.version !== 1) return null;
  if (typeof data.seq !== "number" || !Number.isInteger(data.seq) || data.seq < 0) return null;
  const ids = new Set<string>();
  const root = parseNode(data.root, ids);
  if (!root || ids.size === 0) return null;
  const nav = isObject(data.nav) && isEdge(data.nav.dock) && typeof data.nav.hidden === "boolean" ? { dock: data.nav.dock, hidden: data.nav.hidden } : { dock: "left" as const, hidden: false };
  return normalize({
    version: 1,
    seq: data.seq,
    root,
    focusedId: typeof data.focusedId === "string" ? data.focusedId : "",
    maximizedId: typeof data.maximizedId === "string" ? data.maximizedId : null,
    shelf: parseShelf(data.shelf, ids),
    nav,
    ...(typeof data.layoutId === "string" ? { layoutId: data.layoutId } : {}),
  });
}

/** The stored workspace; else one window on the view the single-view app last showed; else Status. */
export function parseWorkspace(raw: string | null, legacyView?: string | null): Workspace {
  const stored = raw ? parseStored(raw) : null;
  if (stored) return stored;
  if (legacyView) {
    try {
      const view = parseView(JSON.parse(legacyView));
      if (view) return createWorkspace(view);
    } catch {
      // fall through to the default
    }
  }
  return createWorkspace({ kind: "status" });
}

export function serializeWorkspace(ws: Workspace): string {
  return JSON.stringify(ws);
}
